import * as React from "react";

type Bounds = {
  north: number;
  east: number;
  south: number;
  west: number;
} | null;

type LatLng = google.maps.LatLngLiteral;

export function useViewport(
  mapRef: React.MutableRefObject<google.maps.Map | null>,
  initialCenter: LatLng
) {
  const [zoom, setZoom] = React.useState(9);
  const [center, setCenter] = React.useState<LatLng>(initialCenter);
  const [bounds, setBounds] = React.useState<Bounds>(null);
  const idleRaf = React.useRef<number | null>(null);

  const syncFromMap = React.useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const z = map.getZoom();
    if (typeof z === "number") {
      setZoom((prev) => (prev === z ? prev : z));
    }

    const c = map.getCenter();
    if (c) {
      const next = { lat: c.lat(), lng: c.lng() };
      setCenter((prev) =>
        prev.lat === next.lat && prev.lng === next.lng ? prev : next
      );
    }

    const b = map.getBounds();
    if (b) {
      const next = {
        north: b.getNorthEast().lat(),
        east: b.getNorthEast().lng(),
        south: b.getSouthWest().lat(),
        west: b.getSouthWest().lng(),
      };
      setBounds((prev) =>
        prev &&
        prev.north === next.north &&
        prev.east === next.east &&
        prev.south === next.south &&
        prev.west === next.west
          ? prev
          : next
      );
    }
  }, [mapRef]);

  const handleIdle = React.useCallback(() => {
    if (idleRaf.current) cancelAnimationFrame(idleRaf.current);
    idleRaf.current = requestAnimationFrame(syncFromMap);
  }, [syncFromMap]);

  const handleLoad = React.useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      syncFromMap();
    },
    [syncFromMap]
  );

  return {
    zoom,
    center,
    bounds,
    setZoom,
    setCenter,
    setBounds,
    handleIdle,
    handleLoad,
  };
}
